import React, { useState, useEffect } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { formatKey, formatInputToDollar, parseDollarToNumberCalc } from '../../utility/formatting'
import { filterCPFields } from '../../pages/Liquid/utils/displayingCpDetailsHelpers'
import type { ColDef, CellDoubleClickedEvent, ValueFormatterParams, GridApi, EditableCallbackParams, CellClassParams } from 'ag-grid-community'
import type { FilteredCPData, CPInfoComponentProps, RowData } from './types'
import { AllocationCellEditor, AllocationHeaderRenderer } from './renderers'

const CPInfo: React.FC<CPInfoComponentProps> = ({
  data,
  isApprovalScreen = false,
  initialViewMode = 'horizontal',
  disabled = false,
  showCalculatedFields = false,
  calculatedFields = [],
  onCalculatedFieldChange,
  onAllocationsChange,
  showAllocationsAndCalculated = false,
  setCpIdList,
  hasOverAllocation,
  setHasOverAllocation
}) => {
  const filteredCPs: FilteredCPData[] = data.map(cp => ({
    ...filterCPFields(cp, isApprovalScreen),
    cPIssuanceId: cp.cPIssuanceId,
    notionalQtyReminder: cp.notionalQtyReminder,
    discountRateCalc: cp.discountRateCalc,
    tenor: cp.tenor,
    programExpenseDefault: cp.programExpenseDefault
  }))

  const initialAllocations: Record<number, number> = {}
  filteredCPs.forEach(cp => {
    if (cp.cPIssuanceId != null) {
      initialAllocations[cp.cPIssuanceId] = parseDollarToNumberCalc(cp.notionalQtyReminder ?? 0)
    }
  })

  const [viewMode, setViewMode] = useState<'horizontal' | 'vertical'>(initialViewMode)
  const [allocations, setAllocations] = useState<Record<number, number>>(initialAllocations)

  useEffect(() => {
    onAllocationsChange?.(allocations)
    if (setCpIdList) {
      const cpIdListEntries = filteredCPs.map(cp => {
        const allocation = allocations[cp.cPIssuanceId] || 0
        const totalAmount = parseDollarToNumberCalc(cp.notionalQtyReminder)
        return `${cp.cPIssuanceId}:${allocation === totalAmount ? 'ALL' : allocation}`
      })
      setCpIdList(cpIdListEntries.join('|'))
    }
  }, [allocations, onAllocationsChange, setCpIdList, filteredCPs])

  const displayKeys = Object.keys(filterCPFields(data[0] || {}, isApprovalScreen))

  const calculatedFieldDefs = [
    { key: 'proceeds', label: 'Proceeds' },
    { key: 'originalIssueDiscount', label: 'Original Issue Discount' },
    { key: 'originalIssueDiscountPlusProgramExpense', label: 'Program Expense + Original Issue Discount ' },
    { key: 'mmYield', label: 'Interest Rate (mmy)' }
  ]

  const unifiedFieldDefs = [
    ...displayKeys.map(key => ({ key, label: formatKey(key), editable: false })),
    ...(showAllocationsAndCalculated ? [{ key: 'allocationAmount', label: 'Allocate', editable: true }] : []),
    ...(showAllocationsAndCalculated ? calculatedFieldDefs.map(f => ({ ...f, editable: false })) : [])
  ]

  const getCalculatedFieldsForCP = (cp: FilteredCPData): Record<string, number> => {
    const allocationAmount = allocations[cp.cPIssuanceId] ?? parseDollarToNumberCalc(cp.notionalQtyReminder ?? 0)
    const discountRate = Number(cp.discountRateCalc ?? 0)
    const tenor = Number(cp.tenor ?? 0)
    const programExpenseMultiplier = Number(cp.programExpenseDefault ?? 0)

    const originalIssueDiscount = tenor && discountRate && allocationAmount
      ? (allocationAmount * discountRate * tenor) / 36000
      : 0

    const proceeds = allocationAmount - originalIssueDiscount

    const originalIssueDiscountPlusProgramExpense = proceeds && programExpenseMultiplier && tenor
      ? (proceeds * programExpenseMultiplier * tenor) / 360 + originalIssueDiscount
      : originalIssueDiscount

    const mmYield = proceeds && tenor
      ? (originalIssueDiscountPlusProgramExpense * 36000) / (proceeds * tenor)
      : 0

    return {
      proceeds,
      originalIssueDiscount,
      originalIssueDiscountPlusProgramExpense,
      mmYield
    }
  }

  let verticalRowData = unifiedFieldDefs.map(fieldDef => {
    const row: RowData = { field: fieldDef.label }
    filteredCPs.forEach((cp, idx) => {
      const calc = getCalculatedFieldsForCP(cp)
      if (fieldDef.key === 'allocationAmount') {
        row[`cp${idx}`] = allocations[cp.cPIssuanceId] ?? parseDollarToNumberCalc(cp.notionalQtyReminder ?? 0)
      } else if (fieldDef.key in cp) {
        const value = cp[fieldDef.key]
        row[`cp${idx}`] = value ?? ''
      } else if (fieldDef.key in calc) {
        row[`cp${idx}`] = calc[fieldDef.key]
      } else {
        row[`cp${idx}`] = ''
      }
    })
    return row
  })

  verticalRowData = verticalRowData.filter(row => {
    if (row.field === 'Allocate') return true
    return filteredCPs.some((_, idx) => row[`cp${idx}`] !== '' && row[`cp${idx}`] !== null && row[`cp${idx}`] !== undefined)
  })

  const verticalColumnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'field',
      pinned: 'left',
      cellClass: 'font-semibold',
      suppressMovable: true,
      lockPosition: true,
      hide: false,
      cellClassRules: {
        'allocation-cell': (params: CellClassParams) => params.value === 'Allocate'
      },
      cellRenderer: (params: any) => {
        if (params.value === 'Allocate') {
          return (
            <div className="allocation-header">
              <span className="pencil-icon">✎</span>
              <span>{params.value}</span>
            </div>
          )
        }
        return params.value
      }
    },
    ...data.map((cp, idx) => {
      return {
        headerName: cp.counterpartyName ?? `CP ${idx + 1}`,
        field: `cp${idx}`,
        tooltipField: `cp${idx}`,
        minWidth: 220,
        width: 220,
        flex: 1,
        sortable: false,
        suppressMovable: false,
        hide: false,
        cellClass: (params: any) => {
          const field = params.node.data.field
          if ([
            'Proceeds',
            'Allocate',
            'Total Cp Issued',
            'Price',
            'Mm-Yield'
          ].includes(field) || field.includes('Rate') || field.includes('Tenor') || field.includes('Discount')) {
            return 'ag-right-aligned-cell'
          }
          return 'ag-left-aligned-cell'
        },
        suppressKeyboardEvent: (params: any) => {
          return params.event?.key === 'Enter'
        },
        cellStyle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
        tooltipValueGetter: (params: any) => params.value,
        editable: (params: EditableCallbackParams) => params.node?.data?.field === 'Allocate',
        cellClassRules: {
          'allocation-cell': (params: CellClassParams) => params.node?.data?.field === 'Allocate',
          'over-allocation-cell': (params: CellClassParams) => {
            if (params.node?.data?.field !== 'Allocate') return false
            const cpId = params.context?.issuanceIdMap?.[params.column.getColId().replace('cp', '')]
            if (!cpId) return false
            const allocation = params.value
            const notionalQtyReminder = params.context?.notionalQtyReminders?.[cpId] || 0
            return allocation > notionalQtyReminder
          }
        },
        cellEditor: AllocationCellEditor,
        valueFormatter: (params: ValueFormatterParams) => {
          const field = typeof params.node?.data?.field === 'string' ? params.node.data.field : ''
          if (
            ['Proceeds', 'Allocate'].includes(field) ||
            field.includes('Issue Discount')
          ) {
            return '$' + formatInputToDollar(Number(params.value))
          }
          if (field === 'Interest Rate (mmy)') {
            return String(Number(params.value).toFixed(4)) + '%'
          }
          if (field.includes('Rate')) return String(params.value)
          return params.value
        }
      }
    })
  ]

  const horizontalColumnDefs: ColDef[] = [
    ...unifiedFieldDefs.map(fieldDef => {
      let valueFormatter: ColDef['valueFormatter']
      let editable = false
      let cellClass: string | ((params: any) => string) = 'ag-right-aligned-cell'
      if (fieldDef.key === 'mmYield') {
        valueFormatter = (params: ValueFormatterParams) => params.value != null && params.value !== '' ? String(Number(params.value).toFixed(4)) + '%' : ''
      } else if ([
        'proceeds',
        'originalIssueDiscount',
        'originalIssueDiscountPlusProgramExpense',
        'allocationAmount'
      ].includes(fieldDef.key)) {
        valueFormatter = (params: ValueFormatterParams) => params.value != null && params.value !== '' ? '$' + formatInputToDollar(Number(params.value)) : ''
      }
      if (fieldDef.key === 'allocationAmount') {
        editable = true
      }
      if (
        fieldDef.label === 'Proceeds' ||
        fieldDef.label === 'Allocate' ||
        fieldDef.label === 'Total Cp Issued' ||
        fieldDef.label === 'Price' ||
        fieldDef.label === 'Mm-Yield' ||
        fieldDef.label.includes('Rate') ||
        fieldDef.label.includes('Tenor') ||
        fieldDef.label.includes('Discount')
      ) {
        cellClass = 'ag-right-aligned-cell'
      } else if (
        fieldDef.key.toLowerCase().includes('date') ||
        fieldDef.key.toLowerCase().includes('name') ||
        fieldDef.key.toLowerCase().includes('code') ||
        fieldDef.key.toLowerCase().includes('issuer') ||
        fieldDef.key.toLowerCase().includes('counterparty') ||
        fieldDef.key.toLowerCase().includes('security') ||
        fieldDef.key.toLowerCase().includes('cusip')
      ) {
        cellClass = 'ag-left-aligned-cell'
      }
      let columnWidth = 110
      if (!showAllocationsAndCalculated) {
        if (fieldDef.key === 'issuerName' || fieldDef.key === 'counterpartyName') {
          columnWidth = 260
        } else if (fieldDef.key === 'security') {
          columnWidth = 200
        } else {
          columnWidth = 140
        }
      } else {
        if (fieldDef.key === 'issuerName' || fieldDef.key === 'counterpartyName') {
          columnWidth = 230
        } else if (fieldDef.key === 'security' || fieldDef.key === 'totalCpIssued' || fieldDef.key === 'allocationAmount' || fieldDef.key === 'proceeds') {
          columnWidth = 140
        } else if (fieldDef.label.includes('Original Issue Discount')) {
          columnWidth = 120
        }
      }
      return {
        headerName: fieldDef.label,
        field: fieldDef.key,
        minWidth: columnWidth,
        width: columnWidth,
        flex: 0,
        sortable: false,
        suppressMovable: false,
        hide: false,
        cellClass,
        tooltipValueGetter: (params: any) => params.value,
        valueFormatter,
        editable,
        headerComponent: fieldDef.key === 'allocationAmount' ? AllocationHeaderRenderer : undefined,
        cellClassRules: {
          'allocation-cell': (params: CellClassParams) => fieldDef.key === 'allocationAmount',
          'over-allocation-cell': (params: CellClassParams) => {
            if (fieldDef.key !== 'allocationAmount') return false
            const cpId = params.data?.cpId
            if (!cpId) return false
            const allocation = params.value
            const notionalQtyReminder = params.context?.notionalQtyReminders?.[cpId] || 0
            return allocation > notionalQtyReminder
          }
        },
        suppressKeyboardEvent: (params: any) => {
          return params.event?.key === 'Enter'
        },
        cellEditor: AllocationCellEditor
      }
    })
  ]

  const horizontalRowData = filteredCPs.map((cp, idx) => {
    const cpId = typeof cp.cPIssuanceId === 'number' ? cp.cPIssuanceId : 0
    const calc = getCalculatedFieldsForCP(cp)
    const row: Record<string, string | number | null> = {
      cpName: cp.counterpartyName ?? `CP ${idx + 1}`,
      cpId,
      isMaster: 1,
      allocationAmount: allocations[cpId] ?? parseDollarToNumberCalc(cp.notionalQtyReminder ?? 0)
    }
    unifiedFieldDefs.forEach(fieldDef => {
      if (fieldDef.key === 'allocationAmount') {
        row[fieldDef.key] = allocations[cpId] ?? parseDollarToNumberCalc(cp.notionalQtyReminder ?? 0)
      } else if (fieldDef.key in cp) {
        const value = cp[fieldDef.key]
        row[fieldDef.key] = value ?? ''
      } else if (fieldDef.key in calc) {
        row[fieldDef.key] = calc[fieldDef.key]
      } else {
        row[fieldDef.key] = ''
      }
    })
    return row
  })
  const onCellDoubleClicked = (params: CellDoubleClickedEvent) => {
    if (!showCalculatedFields || !params.data.isCalculatedField) return

    const cpId = params.data.cpId
    const fieldName = params.data.fieldName
    const currentValue = params.data.value

    const newValue = prompt('Enter new value:', currentValue.toString())
    if (newValue !== null && !isNaN(Number(newValue))) {
      onCalculatedFieldChange?.(cpId, fieldName, Number(newValue))
    }
  }

  const onGridReady = (params: { api: GridApi }) => {}

  const getDetailRowData = (params: any) => {
    const cpId = params.data.cpId
    return calculatedFields
      .filter(field => field.name !== 'Allocation')
      .map(field => ({
        cpId,
        fieldName: field.name,
        value: field.value,
        isCalculatedField: true,
        isEditable: field.isEditable
      }))
  }

  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message.includes('ResizeObserver')) {
        e.stopImmediatePropagation()
      }
    }
    window.addEventListener('error', handleError)
    return () => {
      window.removeEventListener('error', handleError)
    }
  }, [])

  const issuanceIdMap: Record<string, number> = {}
  filteredCPs.forEach((cp, idx) => {
    if (cp.cPIssuanceId != null) {
      issuanceIdMap[idx.toString()] = cp.cPIssuanceId
    }
  })

  return (
    <div
      className="ag-theme-alpine"
      style={
        viewMode === 'vertical'
          ? {
              minWidth: Math.max(600, 120 + data.length * 300),
              width: Math.max(600, 120 + data.length * 300),
              overflowX: 'auto'
            }
          : { width: '90vw', minWidth: 600, maxWidth: '100%', overflowX: 'auto' }
      }
    >
      <div className="flex justify-between items-center mb-1">
        <h4 className="font-bold">CP Info:</h4>
        <div className="flex gap-2">
          {!disabled && (
            <button
              onClick={() => {
                setViewMode(viewMode === 'vertical' ? 'horizontal' : 'vertical')
              }}
              className="text-sm px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
            >
              {viewMode === 'vertical' ? 'Switch to Horizontal View' : 'Switch to Vertical View'}
            </button>
          )}
        </div>
      </div>
      {hasOverAllocation && (
        <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">
          Warning: One or more allocations exceed availble amount
        </div>
      )}
      <AgGridReact
        columnDefs={viewMode === 'vertical' ? verticalColumnDefs : horizontalColumnDefs}
        rowData={viewMode === 'vertical' ? verticalRowData : horizontalRowData}
        context={{
          issuanceIdMap,
          setAllocations,
          currentAllocations: allocations,
          setHasOverAllocation,
          notionalQtyReminders: filteredCPs.reduce<Record<number, number>>((acc, cp) => {
            if (cp.cPIssuanceId != null) {
              acc[cp.cPIssuanceId] = parseDollarToNumberCalc(cp.notionalQtyReminder ?? 0)
            }
            return acc
          }, {})
        }}
        domLayout="autoHeight"
        headerHeight={32}
        rowHeight={32}
        suppressCellFocus
        onGridReady={onGridReady}
        onCellDoubleClicked={onCellDoubleClicked}
        detailRowHeight={32}
        detailCellRendererParams={{
          getDetailRowData
        }}
        isRowMaster={(params) => {
          if (!params.data) return false
          return showCalculatedFields && params.data.isMaster
        }}
        tooltipShowDelay={0}
        tooltipHideDelay={2000}
        stopEditingWhenCellsLoseFocus={true}
        className={`
          [&_.ag-header-selected]:bg-yellow-200
          [&_.ag-header-cell]:cursor-move
          [&_.ag-header-cell]:after:content-['↔']
          [&_.ag-header-cell]:after:absolute
          [&_.ag-header-cell]:after:right-2
          [&_.ag-header-cell]:after:text-gray-600
          [&_.ag-header-cell]:after:text-sm
          [&_.ag-header-cell]:hover:bg-gray-100
          [&_.ag-header-cell[col-id^='cp']]:cursor-pointer
          [&_.ag-header-cell[col-id='field']]:cursor-default
          [&_.ag-header-cell[col-id='cpName']]:cursor-default
          [&_.ag-header-cell[col-id='field']]:after:hidden
          [&_.ag-header-cell[col-id='cpName']]:after:hidden
          [&_.ag-detail-row]:bg-gray-50
          [&_.ag-detail-cell]:pl-8
          [&_.editable-cell]:cursor-pointer
          [&_.editable-cell]:bg-gray-50
          [&_.editable-cell]:relative
          [&_.editable-cell]:pr-6
          [&_.editable-cell:hover]:bg-gray-200
          [&_.edit-icon]:absolute
          [&_.edit-icon]:right-2
          [&_.edit-icon]:text-gray-600
          [&_.edit-icon]:text-sm
          [&_.edit-icon]:opacity-50
          [&_.edit-icon]:transition-opacity
          [&_.edit-icon]:duration-200
          [&_.editable-cell:hover_.edit-icon]:opacity-100
          [&_.ag-cell-edit-wrapper]:bg-white
          [&_.ag-cell-edit-wrapper]:border
          [&_.ag-cell-edit-wrapper]:border-gray-300
          [&_.ag-cell-edit-wrapper]:rounded
          [&_.ag-cell-edit-wrapper]:p-1
          [&_.ag-cell-edit-wrapper_input]:w-full
          [&_.ag-cell-edit-wrapper_input]:border-0
          [&_.ag-cell-edit-wrapper_input]:outline-none
          [&_.ag-cell-edit-wrapper_input]:p-0.5
          [&_.allocation-cell]:bg-blue-50
          [&_.allocation-cell:hover]:bg-blue-100
          [&_.over-allocation-cell]:bg-red-100
          [&_.over-allocation-cell:hover]:bg-red-200
          [&_.allocation-header]:flex
          [&_.allocation-header]:items-center
          [&_.allocation-header]:gap-1
          [&_.allocation-header]:text-gray-600
          [&_.pencil-icon]:text-sm
          [&_.pencil-icon]:opacity-70
        `}
      />
    </div>
  )
}

export default CPInfo
